import { useState } from "react";
import { BOATS } from "../../data/boats";
import { ArrowRight } from "lucide-react";
import DateRangePicker from "../DateRangePicker";

const GUESTY_BASE = "https://aquastays.guestybookings.com/en/properties/69dff123051f000012a8ddb6";

function buildGuestyUrl(checkIn, checkOut, guests) {
  const params = new URLSearchParams();
  if (checkIn)  params.set("checkIn",  checkIn);
  if (checkOut) params.set("checkOut", checkOut);
  if (guests) {
    params.set("guests", guests);
    params.set("minOccupancy", guests);
  }
  const qs = params.toString();
  return qs ? `${GUESTY_BASE}?${qs}` : GUESTY_BASE;
}

function nightsBetween(a, b) {
  if (!a || !b) return 0;
  return Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));
}

export default function Hero() {
  const hero = BOATS[0].heroImage;
  const [checkIn,  setCheckIn]  = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests,   setGuests]   = useState("2");

  const nights = nightsBetween(checkIn, checkOut);
  const url    = buildGuestyUrl(checkIn, checkOut, guests);

  return (
    <section data-testid="hero-section" id="booking" className="relative h-[82vh] min-h-[560px] flex flex-col justify-between">
      {/* Full-bleed background */}
      <div className="absolute inset-0">
        <img
          src={hero}
          alt="Aquastays houseboat on Lough Ree"
          className="w-full h-full object-cover object-[center_35%]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/20 to-black/55" />
      </div>

      {/* Heading */}
      <div className="relative flex-1 flex items-start justify-center text-center px-6 pt-24 sm:pt-28">
        <div className="max-w-4xl">
          <p className="eyebrow text-white/60 mb-5" data-testid="hero-overline">
            Lough Ree · Glasson · Ireland
          </p>
          <h1
            data-testid="hero-title"
            className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light text-white leading-[1.05] tracking-tight"
          >
            Lazy days<br />
            <em className="italic">on the water.</em>
          </h1>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative px-4 sm:px-10 lg:px-16 pb-8 sm:pb-12">
        <div className="max-w-3xl mx-auto" data-testid="hero-booking-anchor">

          {/* Mobile layout */}
          <div className="flex flex-col sm:hidden bg-white rounded-2xl shadow-2xl overflow-hidden">
            <DateRangePicker
              checkIn={checkIn}
              checkOut={checkOut}
              onChange={({ checkIn: ci, checkOut: co }) => { setCheckIn(ci); setCheckOut(co); }}
            />
            <div className="flex items-center border-t border-gray-100 mx-4" />
            <label className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors">
              <span className="text-[0.55rem] font-semibold uppercase tracking-[0.18em] text-[#1C1C1A]">Guests</span>
              <select
                value={guests}
                onChange={e => setGuests(e.target.value)}
                className="text-sm text-[#6B6B65] font-light bg-transparent focus:outline-none cursor-pointer appearance-none"
              >
                <option value="1">1 guest</option>
                <option value="2">2 guests</option>
              </select>
            </label>
            <div className="p-3 pt-1">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-bar-cta"
                className="flex items-center justify-center gap-2 w-full px-7 py-3 rounded-xl bg-[#E8970A] hover:bg-[#D08500] text-white font-semibold text-sm whitespace-nowrap transition-colors"
              >
                {nights > 0 ? `${nights} night${nights > 1 ? "s" : ""} — search` : "Search"}
                <ArrowRight size={13} />
              </a>
            </div>
          </div>

          {/* Desktop layout — pill */}
          <div className="hidden sm:flex flex-row items-stretch bg-white rounded-full shadow-2xl pl-2 pr-2 py-2 gap-1">
            <DateRangePicker
              checkIn={checkIn}
              checkOut={checkOut}
              onChange={({ checkIn: ci, checkOut: co }) => { setCheckIn(ci); setCheckOut(co); }}
            />
            <div className="flex items-center text-[#1C1C1A]/20 self-center">|</div>
            <label className="flex flex-col justify-center px-5 py-2 cursor-pointer rounded-full hover:bg-gray-50 transition-colors min-w-[110px]">
              <span className="text-[0.55rem] font-semibold uppercase tracking-[0.18em] text-[#1C1C1A] mb-0.5">Guests</span>
              <select
                value={guests}
                onChange={e => setGuests(e.target.value)}
                className="text-sm text-[#6B6B65] font-light bg-transparent focus:outline-none cursor-pointer appearance-none"
              >
                <option value="1">1 guest</option>
                <option value="2">2 guests</option>
              </select>
            </label>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-bar-cta"
              className="flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-[#E8970A] hover:bg-[#D08500] text-white font-semibold text-sm whitespace-nowrap transition-colors self-center shrink-0"
            >
              {nights > 0 ? `${nights} night${nights > 1 ? "s" : ""} — search` : "Search"}
              <ArrowRight size={13} />
            </a>
          </div>

        </div>
      </div>
    </section>
  );
}
